// @ts-check
// =============================================================================
// js/ui/lobby-visibility.js — which lobby rows are too stale to show?
//
// OWNER RULE (client-side only; the server list is untouched)
//   Hide a duel from the lobby list once it has sat idle for 10+ minutes —
//   but ONLY when all three hold:
//     - the match was STARTED (its second seat is taken; a Bot counts, an
//       open "waiting for opponent" seat never age-hides),
//     - the viewer is not a player in it (their own Load/Cancel row must
//       never vanish),
//     - it isn't the game the viewer is currently watching (their live
//       spectate/play view — store.duel's id).
//
//   Staleness reads duel.lastAction, the server-stamped Date.now() of the
//   most recent performed action (BuildPublicDuels ships it). New duels are
//   seeded an hour IN THE FUTURE (duel-setup.js reaper grace), so a fresh
//   game can never hide — the timer only bites after real moves stop.
//
//   Pure + import-free so test/lobby-visibility.test.js can run it in node.
// =============================================================================

/** Idle time after which a foreign, started duel drops off the list. */
export const STALE_LOBBY_MS = 10 * 60_000;

/**
 * Should this lobby row be hidden as stale?
 * @param {any} listed             One BuildPublicDuels row.
 * @param {any} viewerId           The local player's user id.
 * @param {any} currentDuelId      store.duel?.id — the game they're in/watching.
 * @param {number} now             Date.now() (passed in for testability).
 * @returns {boolean}
 */
export function hideStaleDuel(listed, viewerId, currentDuelId, now) {
	const involved = listed.player1?.id === viewerId || listed.player2?.id === viewerId;
	if (involved) return false;
	if (currentDuelId != null && listed.id === currentDuelId) return false;
	const started = !!listed.player2?.id;
	if (!started) return false;
	// No timestamp (defensive: an older server) -> never hide on missing data.
	if (typeof listed.lastAction !== 'number') return false;
	return listed.lastAction < now - STALE_LOBBY_MS;
}

/**
 * Display rank for a lobby row — lower sorts first (owner rule):
 *   0  the viewer's own games (their Load/Cancel rows),
 *   1  awaiting players (open seats someone could join),
 *   2  live PvP matches (worth spectating),
 *   3  bot games (spectate fodder, last).
 * The lobby applies this with a stable sort, so rows keep their newest-first
 * server order within each group.
 * @param {any} listed    One BuildPublicDuels row.
 * @param {any} viewerId  The local player's user id.
 * @returns {number}
 */
export function lobbyRank(listed, viewerId) {
	if (listed.player1?.id === viewerId || listed.player2?.id === viewerId) return 0;
	if (listed.player2?.id === false) return 1;
	if (listed.format === 'Bot') return 3;
	return 2;
}
